import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import ActivityCard from "@/components/app/ActivityCard";
import { Backpack, Clock, CheckCircle2, XCircle, Plus, Sparkles } from "lucide-react";

export default function MyActivities() {
  const { t } = useTranslation();
  const STATUS_STYLES = {
    pending:  { color: "#F9C349", bg: "#FFF1D6", icon: Clock,        label: t("mine.pending") },
    accepted: { color: "#0F9D8F", bg: "#DDF5EA", icon: CheckCircle2, label: t("mine.accepted") },
    rejected: { color: "#D14848", bg: "#FFE6E0", icon: XCircle,      label: t("mine.rejected") },
  };
  const [tab, setTab] = useState("hosting");
  const [hosting, setHosting] = useState(null);
  const [requested, setRequested] = useState(null);

  useEffect(() => {
    api.get("/activities/mine").then((r) => setHosting(r.data)).catch(() => setHosting([]));
    api.get("/requests/outgoing").then((r) => setRequested(r.data)).catch(() => setRequested([]));
  }, []);

  const hostingCount = hosting?.length ?? 0;
  const requestedCount = requested?.length ?? 0;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-32">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#5EEAD4] to-[#14C8B0] flex items-center justify-center shadow-glow">
          <Backpack className="w-6 h-6 text-white" strokeWidth={2.5}/>
        </div>
        <div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-none">{t("mine.title")}</h1>
          <p className="text-sm text-[#4A4F58] mt-1">{t("mine.subtitle")}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-2 glass-strong rounded-full p-1.5 shadow-soft" data-testid="my-activities-tabs">
        <button
          onClick={() => setTab("hosting")}
          data-testid="tab-hosting"
          className={`flex-1 py-2 rounded-full font-display font-bold text-sm transition-all ${
            tab === "hosting" ? "bg-[#0F1115] text-white shadow-soft" : "text-[#4A4F58] hover:text-[#0F1115]"
          }`}
        >
          {t("mine.hosting")} <span className="opacity-60">· {hostingCount}</span>
        </button>
        <button
          onClick={() => setTab("requested")}
          data-testid="tab-requested"
          className={`flex-1 py-2 rounded-full font-display font-bold text-sm transition-all ${
            tab === "requested" ? "bg-[#0F1115] text-white shadow-soft" : "text-[#4A4F58] hover:text-[#0F1115]"
          }`}
        >
          {t("mine.requested")} <span className="opacity-60">· {requestedCount}</span>
        </button>
      </div>

      {/* Hosting tab */}
      {tab === "hosting" && (
        <div className="mt-6 space-y-4" data-testid="hosting-list">
          {hosting === null ? (
            <SkeletonList />
          ) : hosting.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title={t("mine.noActivities")}
              body={t("mine.noActivitiesBody")}
              ctaLabel={t("mine.createActivity")}
              ctaTo="/create"
            />
          ) : (
            hosting.map((a, i) => <ActivityCard key={a.id} activity={a} idx={i}/>)
          )}
        </div>
      )}

      {/* Requested tab */}
      {tab === "requested" && (
        <div className="mt-6 space-y-3" data-testid="requested-list">
          {requested === null ? (
            <SkeletonList />
          ) : requested.length === 0 ? (
            <EmptyState
              icon={Backpack}
              title={t("mine.noRequests")}
              body={t("mine.noRequestsBody")}
              ctaLabel={t("feed.browse")}
              ctaTo="/feed"
            />
          ) : (
            requested.map((r) => <RequestedCard key={r.id} req={r} statusStyles={STATUS_STYLES}/>)
          )}
        </div>
      )}
    </div>
  );
}

function RequestedCard({ req, statusStyles }) {
  const a = req.activity;
  if (!a) return null;
  const s = statusStyles[req.status] || statusStyles.pending;
  const Icon = s.icon;
  const date = a.start_at ? new Date(a.start_at) : null;

  return (
    <Link
      to={`/activity/${a.id}`}
      data-testid={`requested-card-${req.id}`}
      className="card-nb block p-4 hover:-translate-y-0.5 transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-extrabold text-lg leading-tight truncate">{a.title}</h3>
            <span
              className="inline-flex items-center gap-1 text-[11px] font-display font-bold px-2.5 py-1 rounded-full"
              style={{ background: s.bg, color: s.color }}
              data-testid={`req-status-${req.status}`}
            >
              <Icon className="w-3 h-3" strokeWidth={2.75}/> {s.label}
            </span>
          </div>
          <p className="text-xs text-[#4A4F58] mt-1 truncate">
            {a.location || (a.is_online ? "Online" : "—")}
            {date ? ` · ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
            {req.bring_friends ? ` · +${req.bring_friends} friend${req.bring_friends > 1 ? "s" : ""}` : ""}
          </p>
          {req.message && (
            <p className="text-sm text-[#0F1115] mt-2 line-clamp-2 italic">&ldquo;{req.message}&rdquo;</p>
          )}
        </div>
      </div>
    </Link>
  );
}

function EmptyState({ icon: Icon, title, body, ctaLabel, ctaTo }) {
  return (
    <div className="card-nb p-8 text-center" data-testid="empty-state">
      <div className="w-14 h-14 rounded-2xl mx-auto bg-gradient-to-br from-[#DDF5EA] to-[#FFF1D6] flex items-center justify-center mb-3">
        <Icon className="w-7 h-7 text-[#0F9D8F]" strokeWidth={2.25}/>
      </div>
      <h3 className="font-display font-extrabold text-xl">{title}</h3>
      <p className="text-sm text-[#4A4F58] mt-1 mb-4">{body}</p>
      <Link to={ctaTo} className="btn-nb btn-nb-primary inline-flex" data-testid="empty-cta">
        <Plus className="w-4 h-4" strokeWidth={2.5}/> {ctaLabel}
      </Link>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="card-nb p-5 animate-pulse">
          <div className="h-4 w-2/3 bg-[#E5E2D6] rounded mb-3"/>
          <div className="h-3 w-1/2 bg-[#E5E2D6] rounded"/>
        </div>
      ))}
    </div>
  );
}
